import { debounce } from '../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../concepts/commands/CommandDispatcher';
import { Component, ObjectManager } from '../../concepts/objects';
import { Point, PointSource } from '../../platform/geom/Point';
import { Rect } from '../../platform/geom/Rect';
import { SessionStorageService, StorageService } from '../../platform/StorageService';
import { ActiveObject, resolve } from '../Container';
import { optionsPopupWith } from '../popups/OptionsPopup';
import { PopupManager } from '../popups/PopupManager';
import { BoardInfo } from './BoardInfo';
import { Camera, CameraState } from './Camera';
import { CameraStartingPosition } from './CameraStartingPosition';
import { cameraZoomStep, cameraZoomTo } from './commands/CameraCommands';
import { EditorOptions } from './EditorOptions';
import { EditorSession } from './EditorSession';
import { Phys } from './entities/Phys';
import { Physical } from './entities/Physical';
import { timeMachine } from './replay/ReplayCommands';
import { MenuWidget } from './workspace/widgets/WidgetInterface';
import { WidgetManager } from './workspace/widgets/WidgetManager';

// const CameraCalibrator: LFC<{ using?: HTMLElement }> = ({ using }) => {
//     // eslint-disable-next-line @typescript-eslint/no-unused-vars
//     const ws = useWindowSize();
//     const camera = useResolve(Camera);
//     if (using) {
//         invokeLater(() => camera.calibrate(using));
//     }
//     return <></>;
// };

const cameraTargetMissingPopup = () => optionsPopupWith(
    'Object Missing',
    'The object you have been linked to is missing. ' +
    'This is likely because it was deleted. ' +
    'Would you like to view this object in the Time Machine?',
    [
        [true, 'Open Time Machine', 'primary', 'confirm'],
        [false, 'Close', 'common', 'cancel']
    ]
);

const onCalibrate = (callback: (cs: CameraState) => void) => (n: CameraState, p: CameraState) => {
    if (n.ready && !p.ready) {
        callback(n);
    }
};

export class CameraMechanics extends ActiveObject {

    @resolve(Camera) private camera!: Camera;
    @resolve(CommandDispatcher) private dispatch!: CommandDispatcher;
    @resolve(ObjectManager) private objects!: ObjectManager;
    @resolve(EditorOptions) private options!: EditorOptions;
    @resolve(PopupManager) private popups!: PopupManager;
    @resolve(EditorSession) private session!: EditorSession;
    @resolve(SessionStorageService) private storage!: StorageService;
    @resolve(WidgetManager) private widgets!: WidgetManager;

    public start() {
        this.burden(
            this.camera.observe(onCalibrate(this.initCameraState)),
            this.camera.observe(debounce(this.saveCameraState.bind(this), 250)),
            this.camera.observe(this.updateZoomWidgets),
        );
        this.realizeZoomWidgets();
    }

    private initCameraState = () => {
        const instr = this.options.camera ?? '';
        if (instr?.startsWith('auto')) {
            const [_, rule] = instr.split(':');
            this.initCameraFromFrame(this.computeAutoFrame(rule));
            this.showTargetMissingPopupIfApplicable(rule);
        }
        else if (instr.match(/-?\d+,-?\d+,\d+,\d+/)) {
            this.initCameraFromFrame(this.parseExplicitFrame(instr));
        }
        else {
            const { position, scale } = this.loadSavedCameraState();
            if (position || scale) {
                this.initCameraFromPositionAndScale(position, scale);
            }
            else {
                this.initCameraFromFrame(this.loadStartingFrame());
            }
        }
    };

    private saveCameraState({ ready, position, scale }: CameraState) {
        if (!ready) return false;
        const boardInfo = this.session.get(BoardInfo);
        this.storage.put(`deqo/${boardInfo.id}/camera`, { position, scale });
    }

    private initCameraFromFrame(frame?: Rect, padFactor?: number) {
        if (frame) {
            this.camera.setFrame(frame, padFactor);
            this.camera.zoomTo(Math.max(Math.min(this.camera.scale, 1), 0.05));
        }
    }

    private initCameraFromPositionAndScale(position?: PointSource, scale?: number) {
        const rmp = new Point(10000, 10000);
        this.camera.setView(
            Point.from((position ?? Point.origin)).clamp(rmp.multiply(-1), rmp.multiply(1)),
            Math.min(Math.max(scale ?? 1, 0.1), 4),
        );
    }

    private loadSavedCameraState() {
        const boardInfo = this.session.get(BoardInfo);
        return this.storage.get(`deqo/${boardInfo.id}/camera`) ?? {};
    }

    private loadStartingFrame() {
        const [csp] = Array.from(this.objects.getAll(CameraStartingPosition)).sort((a, b) => a.id.localeCompare(b.id));
        return csp?.toRect() || this.computeAutoFrame();
    }

    private computeAutoFrame(rule?: string) {

        const ruleFilter = (
            (rule === 'ignore-text' ? ((x: Component) => x.type !== 'Text') : null) ??
            (rule?.includes('/') ? ((x: Component) => x.id === rule) : null) ??
            (() => true)
        );

        const elmts = Array.from(this.objects.getAll())
            .filter(ruleFilter)
            .map(x => {
                if (x.is(Physical)) return x;
                if (x.parent?.is(Physical)) return x.parent;
                if (x.parent?.parent?.is(Physical)) return x.parent.parent;
                return null;
            })
            .filter(x => !!x?.is(Physical));

        return Phys.from(elmts).bounds();
    }

    private parseExplicitFrame(input: string) {
        const [l, t, w, h] = input.split(/,/g).map(x => parseInt(x));
        const frame = new Rect(l, t, w, h);
        return frame;
    }

    private realizeZoomWidgets = () => {
        const { dispatch, widgets } = this;

        widgets.delete('zoom');

        const zoom = widgets.createContainer();
        zoom.name = 'zoom';
        zoom.weight = 100;
        zoom.style = 'floatWithLines';
        zoom.position = ['docked', 'bottomLeft', 'inline'];

        // Zoom Out
        zoom.add(widgets.createButton({
            name: 'zoomOut',
            text: 'Zoom Out',
            image: 'deqo://icons/deqo/subtract@[0.75,0.75,0]',
            onAction() {
                dispatch(cameraZoomStep, [-1]);
            }
        }));

        const levelButton = (z: number) =>
            widgets.createButton({
                text: `${z}%`,
                onAction: () => {
                    dispatch(cameraZoomTo, [z / 100]);
                    level.update({ open: false });
                }
            });

        const level = widgets.createMenu({
            name: 'level',
            width: 5,
            itemsWidth: 8,
            text: '100%',
            items: [
                levelButton(200),
                levelButton(150),
                levelButton(100),
                levelButton(75),
                levelButton(50),
                levelButton(25),
            ]
        });
        zoom.add(level);

        // Zoom In
        zoom.add(widgets.createButton({
            name: 'zoomIn',
            text: 'Zoom In',
            image: 'deqo://icons/deqo/add@[0.75,0.75,0]',
            onAction() {
                dispatch(cameraZoomStep, [1]);
            }
        }));

        widgets.add(zoom);
    };

    private updateZoomWidgets = (next: CameraState, prev: CameraState) => {
        if (next.scale === prev.scale) return;
        const level = this.widgets.get('zoom')?.get('level') as MenuWidget;
        if (!level) return;
        level.update({ text: `${Math.round(next.scale * 100)} %` });
    };

    private async showTargetMissingPopupIfApplicable(rule?: string) {
        if (rule?.includes('/')) {
            const elmt = this.objects.getMaybe(rule);
            if (!elmt) {
                const { actioned, data } = await this.popups.invoke(cameraTargetMissingPopup());
                if (actioned && data) {
                    this.dispatch(timeMachine, [rule]);
                }
            }
        }
    }
}
